<template>
  <div class="layer-color-cost-calculation map-filter-container layer-detail-content">
    <div class="ld-input-holder">
      <div class="input_label">{{ $t('layer_colorizing.select_type') }}</div>
      <div class="cost-container">
        <DxSelectBox
            :items="cost_types"
            :placeholder="'-'+$t('layer_colorizing.select')+'-'"
            valueExpr="value"
            displayExpr="label"
            :disabled="isCostDataLoading"
            v-model="selectedType"
        />
      </div>

      <div class="cost-container-selector" v-if="selectedType">
        <div class="cost-step-container" v-if="currentSelectedType.step1_data">
          <div class="step1-date-range font-weight-bold" v-if="dateRange_local">
            <h5>{{ $t('layer_colorizing.selected_data_from_previous_step', { type: currentSelectedType.step1_text }) }}</h5>
            <div class="fw-bold">{{ $t('layer_colorizing.filters.date_interval') }}</div>
            <p>{{ dateRange_local.date_from +' - '+dateRange_local.date_to }}</p>
          </div>
          <div class="step1-selection" v-if="colorFilterItemsSelections_local && colorFilterItemsSelections_local.length > 0">
            <div class="fw-bold">{{ currentSelectedType.step1_text }}</div>
            <p>{{ colorFilterItemsSelections_local[0].name }}</p>
          </div>
          <p>
            <a v-on:click.prevent="backToPrevStep()" class="c-pointer text-primary p-2 fw-bold" v-if="!prevStepSelection">
              {{ $t('layer_colorizing.select_from_previous_step', { type: currentSelectedType.step1_text }) }}
            </a>
            <a v-on:click.prevent="backToPrevStep()" class="c-pointer text-primary fw-bold" v-else>
              {{ $t('layer_colorizing.change_selection') }}
            </a>
          </p>
          <layer-color-filter-element
              v-if="dateRange_local && colorFilterItemsSelections_local && colorFilterItemsSelections_local.length > 0 && !isCostDataLoading"
              :items="costRangeList"
              @onChange="onCostRangeSelectionChanged"
              :multi-select="true"
              :hide-calendar="true"
              :hide-search="true"
              :error-message="colorFilterElementErrorMessage"
          />
          <div class="text-center py-2" v-if="isCostDataLoading">
            <loader :loading="isCostDataLoading" size="sm"/>
          </div>
        </div>
        <div class="regular-cost-selector" v-else>
          <layer-color-filter-element
              v-if="!isCostDataLoading"
              :items="costRangeList"
              @onChange="onCostRangeSelectionChanged"
              :multi-select="true"
              :hide-calendar="true"
              :error-message="colorFilterElementErrorMessage"
          />
          <div class="text-center py-2" v-if="isCostDataLoading">
            <loader :loading="isCostDataLoading" size="sm"/>
          </div>
        </div>
      </div>

      <div class="cost-calculation-footer">
        <b-button
            v-if="(selectedType && !currentSelectedType.step1_data) || ( selectedType && currentSelectedType.step1_data && dateRange_local && colorFilterItemsSelections_local && colorFilterItemsSelections_local.length > 0)"
            class="continue_button"
            variant="outline-secondary"
            size="sm"
            pill
          :disabled="isCostDataLoading"
            @click="applyCalculationFilter">{{ $t('apply') }}</b-button>
      </div>
    </div>
  </div>
</template>

<script>
import DxSelectBox from "devextreme-vue/select-box";
import LayerColorFilterElement from "@/components/Map/LayerColorFilterElement.vue";
import Loader from "@/components/Loader";
import {getColorFilterOption} from "@/helpers/googleMapHelper";
import {numberFormat} from "@/plugins/filters";

export default {
  name: 'LayerColorCostCalculation',
  props: {
    dateRange: {
      type: Object,
    },
    getLayers: {
      type: Function|null,
    },
    getCostLayers: {
      type: Function|null,
    },
    colorFilterItemsSelections: {
      type: Array|null,
      default: () => [],
    },
    currentTab: {
      type: String,
    }
  },
  components: {LayerColorFilterElement, DxSelectBox, Loader},
  data() {
    return {
      selectedType: null,
      cost_types: [
        {
          value: 'total_cost',
          label: this.$t('layer_colorizing.filters.cost_total')
        },
        {
          value: 'cost_per_ha',
          label: this.$t('layer_colorizing.filters.cost_per_ha')
        },
        {
          value: 'cost_per_kg',
          label: this.$t('layer_colorizing.filters.cost_per_kg')
        },
        {
          value: 'cost_product',
          label: this.$t('layer_colorizing.filters.cost_product'),
          step1_data: true,
          step1_type: 'PRODUCT',
          step1_tab: 'STRATIFICARE',
          step1_text: this.$t('layer_colorizing.filters.product')
        },
        {
          value: 'cost_product_type',
          label: this.$t('layer_colorizing.filters.cost_product_type'),
          step1_data: true,
          step1_type: 'PRODUCT_TYPE',
          step1_tab: 'STRATIFICARE',
          step1_text: this.$t('layer_colorizing.filters.product_type'),
        },
      ],
      colorFilterElementErrorMessage: null,
      isCostDataLoading: false,
      total_cost_layers: [],
      selected_cost_ranges: [],
      costRangeList: [],
      predefinedRanges: [
          //includes min_value, max_value (amount)
        { min: 0, max: 25, color: '#f1f344' },
        { min: 25, max: 50, color: '#ffc107' },
        { min: 50, max: 75, color: '#d526d5f5' },
        { min: 75, max: 100, color: '#dc3545' }
      ],
      mergeProfiles: {
        total_cost: {
          minCount: 20,
          maxShare: 0.12,
          minCap: 15,
          maxCap: 40,
        },
        cost_per_ha: {
          minCount: 10,
          maxShare: 0.10,
          minCap: 12,
          maxCap: 30,
        },
        cost_per_kg: {
          minCount: 10,
          maxShare: 0.10,
          minCap: 12,
          maxCap: 30,
        }
      },
      imbalanceThreshold: 0.8,
      maxAdaptiveSplits: 4,
      maxImbalanceRebalanceSplits: 12,
      maxFinalRanges: 24,
      costPerHaQuantileBins: 6,
      costPerHaOutlierPercentile: 0.99,
      autoSelect: true,
      colorFilterItemsSelections_local: this.colorFilterItemsSelections_local ?? [],
      dateRange_local: this.dateRange_local ?? null,
    }
  },
  computed: {
    currentSelectedType() {
      return this.cost_types.find(c => c.value === this.selectedType);
    },
    prevStepSelection() {
      return this.dateRange_local && this.colorFilterItemsSelections_local.length > 0;
    }
  },
  methods: {
    hexToRgb(hex) {
      const cleaned = String(hex || '').replace('#', '');
      const normalized = cleaned.length === 3
        ? cleaned.split('').map(ch => ch + ch).join('')
        : cleaned;

      const value = Number.parseInt(normalized, 16);
      if (!Number.isFinite(value)) {
        return { r: 0, g: 0, b: 0 };
      }

      return {
        r: (value >> 16) & 255,
        g: (value >> 8) & 255,
        b: value & 255,
      };
    },
    rgbToHex(r, g, b) {
      const toHex = (value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0');
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    },
    interpolateColor(fromHex, toHex, t) {
      const from = this.hexToRgb(fromHex);
      const to = this.hexToRgb(toHex);
      const clampedT = Math.max(0, Math.min(1, t));

      return this.rgbToHex(
        from.r + ((to.r - from.r) * clampedT),
        from.g + ((to.g - from.g) * clampedT),
        from.b + ((to.b - from.b) * clampedT),
      );
    },
    interpolateColorStops(stops, t) {
      const segments = stops.length - 1;
      if (segments <= 0) {
        return stops[0];
      }

      const scaled = this.clamp(t, 0, 1) * segments;
      const index = Math.min(Math.floor(scaled), segments - 1);

      return this.interpolateColor(stops[index], stops[index + 1], scaled - index);
    },
    toCostNumber(value) {
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
      }

      if (typeof value === 'string') {
        const normalized = value.trim().replace(/,/g, '');
        const parsed = Number.parseFloat(normalized);

        return Number.isFinite(parsed) ? parsed : 0;
      }

      return 0;
    },
    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    },
    getMergeConfig(costField, totalLayers) {
      const profile = this.mergeProfiles[costField] || this.mergeProfiles.total_cost;
      const rawMaxMergedCount = Math.round(totalLayers * profile.maxShare);
      const maxMergedCount = this.clamp(rawMaxMergedCount, profile.minCap, profile.maxCap);

      return {
        minCount: profile.minCount,
        maxMergedCount,
      };
    },
    setColorFilterError(errorResponse) {
      if (errorResponse?.response?.status === 500 && errorResponse?.response?.data?.errors) {
        this.colorFilterElementErrorMessage = this.$t('general_error');
        return;
      }

      this.colorFilterElementErrorMessage = this.$t('general_error');
    },
    applyCalculationFilter() {
      this.$emit('onUpdatedCostRangeLayers', this.total_cost_layers, this.selected_cost_ranges);
    },
    //cost calculation results
    getCostRangesAndColors(min, max, prefix = 'range') {
      const safeMin = this.toCostNumber(min);
      const safeMax = this.toCostNumber(max);
      const interval = Math.max(safeMax - safeMin, 0);
      const unit = this.selectedType === 'cost_per_kg' ? ' RON/kg' : ' RON';

      return this.predefinedRanges.map((item, index) => {
        const min_value = safeMin + ((interval * item.min) / 100);
        const max_value = safeMin + ((interval * item.max) / 100);
        const uuid = `${prefix}_${index}_${min_value}_${max_value}`;
        const base_name = numberFormat(min_value) + ' - ' + numberFormat(max_value) + unit;

        return {
          ...item,
          min_value,
          max_value,
          uuid,
          name: base_name,
          base_name,
          selected: this.autoSelect,
          disabled: max_value === 0,
        };
      });
    },
    getDominantImbalanceIndex(ranges, countsByRange) {
      let candidate = null;

      for (let i = 0; i < ranges.length - 1; i++) {
        const left = countsByRange[ranges[i].uuid] || 0;
        const right = countsByRange[ranges[i + 1].uuid] || 0;
        const total = left + right;

        if (total <= 0) {
          continue;
        }

        const dominantShare = Math.max(left, right) / total;
        if (dominantShare < this.imbalanceThreshold) {
          continue;
        }

        const dominantIndex = left >= right ? i : i + 1;
        const dominantCount = Math.max(left, right);

        if (!candidate || dominantShare > candidate.dominantShare ||
          (dominantShare === candidate.dominantShare && dominantCount > candidate.dominantCount)) {
          candidate = {
            index: dominantIndex,
            dominantShare,
            dominantCount,
          };
        }
      }

      return candidate?.index;
    },
    getDominantShare(leftCount, rightCount) {
      const total = leftCount + rightCount;
      if (total <= 0) {
        return 0;
      }

      return Math.max(leftCount, rightCount) / total;
    },
    getDynamicMaxMergedCount(ranges, countsByRange, index, fallbackMaxMergedCount) {
      const ratioLimit = this.imbalanceThreshold / (1 - this.imbalanceThreshold);
      let dynamicMax = fallbackMaxMergedCount;
      const prevRange = ranges[index - 1];
      const nextRange = ranges[index + 2];

      if (prevRange) {
        const prevCount = countsByRange[prevRange.uuid] || 0;
        if (prevCount > 0) {
          dynamicMax = Math.min(dynamicMax, Math.floor(prevCount * ratioLimit));
        }
      }

      if (nextRange) {
        const nextCount = countsByRange[nextRange.uuid] || 0;
        if (nextCount > 0) {
          dynamicMax = Math.min(dynamicMax, Math.floor(nextCount * ratioLimit));
        }
      }

      return Math.max(dynamicMax, 0);
    },
    getPercentileValue(sortedValues, percentile) {
      if (!Array.isArray(sortedValues) || sortedValues.length === 0) {
        return 0;
      }

      const clampedPercentile = this.clamp(percentile, 0, 1);
      const index = Math.floor((sortedValues.length - 1) * clampedPercentile);

      return sortedValues[index];
    },
    buildQuantileRanges(layers, costField, bins = 6, prefix = 'quantile') {
      if (!Array.isArray(layers) || layers.length === 0) {
        return [];
      }

      const values = layers
        .map((layer) => this.toCostNumber(layer[costField]))
        .filter((value) => Number.isFinite(value) && value >= 0)
        .sort((a, b) => a - b);

      if (values.length === 0) {
        return [];
      }

      const minValue = values[0];
      const maxValue = values[values.length - 1];
      if (maxValue <= minValue) {
        const baseName = numberFormat(minValue) + ' - ' + numberFormat(maxValue) + (this.selectedType === 'cost_per_kg' ? ' RON/kg' : ' RON');
        return [{
          min_value: minValue,
          max_value: maxValue,
          uuid: `${prefix}_0_${minValue}_${maxValue}`,
          name: `${baseName} (${values.length} parcella)`,
          base_name: baseName,
          color: '#f1f344',
          selected: this.autoSelect,
          disabled: maxValue === 0,
        }];
      }

      const outlierThreshold = this.getPercentileValue(values, this.costPerHaOutlierPercentile);
      const hasOutlierTail = outlierThreshold > minValue && outlierThreshold < maxValue;
      const coreValues = hasOutlierTail
        ? values.filter((value) => value <= outlierThreshold)
        : values;

      const coreMin = coreValues[0] ?? minValue;
      const coreMax = coreValues[coreValues.length - 1] ?? maxValue;

      const safeBins = this.clamp(Math.floor(bins), 2, 12);
      const boundaries = [];
      for (let i = 1; i < safeBins; i++) {
        boundaries.push(this.getPercentileValue(coreValues, i / safeBins));
      }

      const uniqueBoundaries = [...new Set(boundaries)]
        .filter((value) => value > coreMin && value < coreMax)
        .sort((a, b) => a - b);

      const edges = [coreMin, ...uniqueBoundaries, coreMax];
      const ranges = [];
      const unit = this.selectedType === 'cost_per_kg' ? ' RON/kg' : ' RON';

      for (let i = 0; i < edges.length - 1; i++) {
        const rangeMin = edges[i];
        const rangeMax = edges[i + 1];

        if (rangeMax <= rangeMin) {
          continue;
        }

        const baseName = numberFormat(rangeMin) + ' - ' + numberFormat(rangeMax) + unit;
        ranges.push({
          min_value: rangeMin,
          max_value: rangeMax,
          uuid: `${prefix}_${i}_${rangeMin}_${rangeMax}`,
          name: baseName,
          base_name: baseName,
          selected: this.autoSelect,
          disabled: rangeMax === 0,
        });
      }

      if (hasOutlierTail && maxValue > coreMax) {
        const baseName = numberFormat(coreMax) + ' - ' + numberFormat(maxValue) + unit;
        ranges.push({
          min_value: coreMax,
          max_value: maxValue,
          uuid: `${prefix}_outlier_${coreMax}_${maxValue}`,
          name: baseName,
          base_name: baseName,
          selected: this.autoSelect,
          disabled: maxValue === 0,
          isOutlier: true,
        });
      }

      return ranges;
    },
    isMergeBalancedWithNeighbors(ranges, countsByRange, index, mergedCount) {
      const prevRange = ranges[index - 1];
      const nextRange = ranges[index + 2];

      if (prevRange) {
        const prevCount = countsByRange[prevRange.uuid] || 0;
        const prevShare = this.getDominantShare(prevCount, mergedCount);
        if (prevShare > this.imbalanceThreshold) {
          return false;
        }
      }

      if (nextRange) {
        const nextCount = countsByRange[nextRange.uuid] || 0;
        const nextShare = this.getDominantShare(nextCount, mergedCount);
        if (nextShare > this.imbalanceThreshold) {
          return false;
        }
      }

      return true;
    },
    buildAdaptiveRanges(maxValue, layers, costField) {
      let ranges = this.getCostRangesAndColors(0, maxValue, 'base');
      let splitCount = 0;

      while (splitCount < this.maxAdaptiveSplits) {
        const countsByRange = this.getRangeCounts(ranges, layers, costField);
        const splitIndex = this.getDominantImbalanceIndex(ranges, countsByRange);

        if (splitIndex === undefined) {
          break;
        }

        const targetRange = ranges[splitIndex];
        const subRanges = this.getCostRangesAndColors(
          targetRange.min_value,
          targetRange.max_value,
          targetRange.uuid
        );

        ranges = [
          ...ranges.slice(0, splitIndex),
          ...subRanges,
          ...ranges.slice(splitIndex + 1),
        ];

        splitCount++;
      }

      return ranges;
    },
    applyProgressiveColors(ranges) {
      if (!Array.isArray(ranges) || ranges.length === 0) {
        return [];
      }

      const sorted = [...ranges].sort((a, b) => a.min_value - b.min_value);
      // Quantile buckets are count-balanced, not value-balanced, so coloring by
      // actual value position squashes several low buckets into near-identical
      // shades. Coloring by rank instead guarantees every bucket - including the
      // outlier tail, which naturally lands last - gets a distinct, monotonically
      // darker color from light yellow to dark red.
      const colorStops = ['#f1f344', '#f5a623', '#dc3545', '#7a0d17'];
      const denominator = Math.max(sorted.length - 1, 1);

      return sorted.map((range, index) => ({
        ...range,
        color: this.interpolateColorStops(colorStops, index / denominator),
      }));
    },
    mergeSmallAdjacentRanges(ranges, layers, costField, minCount = 20, maxMergedCount = 30) {
      if (!Array.isArray(ranges) || ranges.length <= 1) {
        return ranges;
      }

      const merged = [...ranges].sort((a, b) => a.min_value - b.min_value);
      let changed = true;

      while (changed) {
        changed = false;
        const countsByRange = this.getRangeCounts(merged, layers, costField);

        for (let i = 0; i < merged.length - 1; i++) {
          const left = merged[i];
          const right = merged[i + 1];
          const leftCount = countsByRange[left.uuid] || 0;
          const rightCount = countsByRange[right.uuid] || 0;
          const combinedCount = leftCount + rightCount;
          const dynamicMaxMergedCount = this.getDynamicMaxMergedCount(
            merged,
            countsByRange,
            i,
            maxMergedCount,
          );

          if (
            leftCount < minCount &&
            rightCount < minCount &&
            combinedCount <= dynamicMaxMergedCount &&
            this.isMergeBalancedWithNeighbors(merged, countsByRange, i, combinedCount)
          ) {
            const minValue = Math.min(left.min_value, right.min_value);
            const maxValue = Math.max(left.max_value, right.max_value);
            const baseName = numberFormat(minValue) + ' - ' + numberFormat(maxValue) + ' RON';

            const combined = {
              ...left,
              min_value: minValue,
              max_value: maxValue,
              uuid: `${left.uuid}__${right.uuid}`,
              base_name: baseName,
              name: baseName,
              selected: this.autoSelect,
              disabled: maxValue === 0,
            };

            merged.splice(i, 2, combined);
            changed = true;
            break;
          }
        }
      }

      return merged;
    },
    removeEmptyRanges(ranges, layers, costField) {
      if (!Array.isArray(ranges) || ranges.length === 0) {
        return [];
      }

      const sorted = [...ranges].sort((a, b) => a.min_value - b.min_value);
      const countsByRange = this.getRangeCounts(sorted, layers, costField);
      const nonEmpty = sorted.filter((range) => (countsByRange[range.uuid] || 0) > 0);

      if (nonEmpty.length === 0) {
        return [sorted[0]];
      }

      return nonEmpty;
    },
    mergeUnderfilledRanges(ranges, layers, costField, minRangeCount = 2) {
      if (!Array.isArray(ranges) || ranges.length <= 1) {
        return ranges;
      }

      const merged = [...ranges].sort((a, b) => a.min_value - b.min_value);
      let changed = true;

      while (changed) {
        changed = false;
        const countsByRange = this.getRangeCounts(merged, layers, costField);

        for (let i = 0; i < merged.length; i++) {
          const current = merged[i];
          const currentCount = countsByRange[current.uuid] || 0;

          if (currentCount >= minRangeCount) {
            continue;
          }

          if (merged.length <= 1) {
            return merged;
          }

          const leftIndex = i - 1;
          const rightIndex = i + 1;
          let targetIndex = null;

          if (leftIndex >= 0 && rightIndex < merged.length) {
            const leftCount = countsByRange[merged[leftIndex].uuid] || 0;
            const rightCount = countsByRange[merged[rightIndex].uuid] || 0;
            targetIndex = leftCount <= rightCount ? leftIndex : rightIndex;
          } else if (leftIndex >= 0) {
            targetIndex = leftIndex;
          } else if (rightIndex < merged.length) {
            targetIndex = rightIndex;
          }

          if (targetIndex === null) {
            continue;
          }

          const target = merged[targetIndex];
          const minValue = Math.min(current.min_value, target.min_value);
          const maxValue = Math.max(current.max_value, target.max_value);
          const baseName = numberFormat(minValue) + ' - ' + numberFormat(maxValue) + ' RON';
          const combined = {
            ...target,
            min_value: minValue,
            max_value: maxValue,
            uuid: `${target.uuid}__${current.uuid}`,
            base_name: baseName,
            name: baseName,
            selected: this.autoSelect,
            disabled: maxValue === 0,
          };

          const first = Math.min(i, targetIndex);
          const second = Math.max(i, targetIndex);
          merged.splice(second, 1);
          merged.splice(first, 1, combined);

          changed = true;
          break;
        }
      }

      return merged;
    },
    rebalanceImbalancedRanges(ranges, layers, costField) {
      if (!Array.isArray(ranges) || ranges.length <= 1) {
        return ranges;
      }

      let balanced = [...ranges].sort((a, b) => a.min_value - b.min_value);
      let splitCount = 0;

      while (splitCount < this.maxImbalanceRebalanceSplits && balanced.length < this.maxFinalRanges) {
        const countsByRange = this.getRangeCounts(balanced, layers, costField);
        const splitIndex = this.getDominantImbalanceIndex(balanced, countsByRange);

        if (splitIndex === undefined) {
          break;
        }

        const targetRange = balanced[splitIndex];
        const targetCount = countsByRange[targetRange.uuid] || 0;
        if (targetCount <= 1) {
          break;
        }

        const subRanges = this.getCostRangesAndColors(
          targetRange.min_value,
          targetRange.max_value,
          `${targetRange.uuid}_reb`
        );

        const subCounts = this.getRangeCounts(subRanges, layers, costField);
        const nonEmptySubRanges = subRanges.filter((range) => (subCounts[range.uuid] || 0) > 0);

        if (nonEmptySubRanges.length <= 1) {
          break;
        }

        balanced = [
          ...balanced.slice(0, splitIndex),
          ...subRanges,
          ...balanced.slice(splitIndex + 1),
        ];
        splitCount++;
      }

      return balanced;
    },
    getRangeCounts(ranges, layers, costField) {
      return layers.reduce((acc, layer) => {
        if (layer[costField] === null || layer[costField] === undefined) {
          return acc;
        }
        const cost = this.toCostNumber(layer[costField]);
        const range = this.getConstRange(cost, ranges);

        if (range) {
          acc[range.uuid] = (acc[range.uuid] || 0) + 1;
        }

        return acc;
      }, {});
    },
    withRangeParcelCounts(ranges, layers, costField) {
      const countsByRange = this.getRangeCounts(ranges, layers, costField);

      return ranges.map((range) => {
        const count = countsByRange[range.uuid] || 0;
        const unit = costField === 'cost_per_kg' ? ' RON/kg' : ' RON';
        return {
          ...range,
          name: `${range.base_name}${unit} (${count} parcella)`
        };
      });
    },
    getConstRange(layerCost, ranges = this.costRangeList) {
      const normalizedLayerCost = this.toCostNumber(layerCost);

      return ranges.find((item, index) => {
        const isLast = index === ranges.length - 1;
        return normalizedLayerCost >= item.min_value &&
          (isLast ? normalizedLayerCost <= item.max_value : normalizedLayerCost < item.max_value);
      });
    },
    loadCostTotalData(costType, params = {}) {
      const useDedicatedCostLayers =
        (costType === 'total_cost' || costType === 'cost_per_ha' || costType === 'cost_per_kg')
        && this.getCostLayers instanceof Function;
      const loadLayersFn = useDedicatedCostLayers ? this.getCostLayers : this.getLayers;
      this.isCostDataLoading = true;
      this.colorFilterElementErrorMessage = null;

      if (loadLayersFn instanceof Function) {
        loadLayersFn({...{attach_query: costType, stopFitBounds: true}, ...params}).then((resp) => {

          if (costType !== 'total_cost' && costType !== 'cost_per_kg') {
            costType = 'cost_per_ha'; //every cost calc by cost per ha, els calc total cost
          }

          const layers = Array.isArray(resp)
            ? resp
            : (Array.isArray(resp?.data) ? resp.data : []);

          const maxItem = layers.reduce((max, item) =>
                  (!max || this.toCostNumber(item[costType]) > this.toCostNumber(max[costType])) ? item : max
              , null);

          const maxValue = maxItem ? this.toCostNumber(maxItem[costType]) : 0;
          const minValue = layers.length > 0
            ? layers.reduce((min, item) => {
              const value = this.toCostNumber(item[costType]);
              return min === null || value < min ? value : min;
            }, null)
            : 0;
          const hasPositiveCost = layers.some((item) => this.toCostNumber(item[costType]) > 0);

          if (layers.length === 0 || !hasPositiveCost) {
            this.costRangeList = [];
            this.selected_cost_ranges = [];
            this.total_cost_layers = Array.isArray(layers)
              ? layers.map((layer) => ({
                ...layer,
                cost_range: null,
                options: getColorFilterOption('#D3D3D3'),
                selected: false,
                selected_type: this.selectedType,
              }))
              : [];
            this.colorFilterElementErrorMessage = this.$t('layer_colorizing.no_cost_data_for_selection');
            return;
          }

          let costRanges = this.getCostRangesAndColors(0, maxValue);
          if (this.selectedType === 'total_cost' || this.selectedType === 'cost_per_ha' || this.selectedType === 'cost_per_kg') {
            if (costType === 'cost_per_ha') {
              costRanges = this.buildQuantileRanges(layers, costType, this.costPerHaQuantileBins);
            } else if (costType === 'cost_per_kg') {
              // Parcels without harvest have cost_per_kg = NULL and must not
              // participate in range building (they are not 0 RON/kg).
              const layersWithHarvest = layers.filter(
                (layer) => layer[costType] !== null && layer[costType] !== undefined
              );
              costRanges = this.buildQuantileRanges(layersWithHarvest, costType, this.costPerHaQuantileBins);
            } else {
              const mergeConfig = this.getMergeConfig(costType, layers.length);
              costRanges = this.buildAdaptiveRanges(maxValue, layers, costType);
              costRanges = this.mergeSmallAdjacentRanges(
                costRanges,
                layers,
                costType,
                mergeConfig.minCount,
                mergeConfig.maxMergedCount,
              );
              costRanges = this.removeEmptyRanges(costRanges, layers, costType);
              const minRangeCount = 1;
              costRanges = this.mergeUnderfilledRanges(costRanges, layers, costType, minRangeCount);
              costRanges = this.rebalanceImbalancedRanges(costRanges, layers, costType);
              costRanges = this.removeEmptyRanges(costRanges, layers, costType);
            }
          }

          if ((!Array.isArray(costRanges) || costRanges.length === 0) && layers.length > 0) {
            const fallbackMin = this.toCostNumber(minValue);
            const fallbackMax = this.toCostNumber(maxValue);
            const fallbackUnit = this.selectedType === 'cost_per_kg' ? ' RON/kg' : ' RON';

            if (fallbackMax <= fallbackMin) {
              const baseName = numberFormat(fallbackMin) + ' - ' + numberFormat(fallbackMax) + fallbackUnit;
              costRanges = [{
                min_value: fallbackMin,
                max_value: fallbackMax,
                uuid: `fallback_${fallbackMin}_${fallbackMax}`,
                name: baseName,
                base_name: baseName,
                selected: this.autoSelect,
                disabled: fallbackMax === 0,
              }];
            } else {
              costRanges = this.getCostRangesAndColors(fallbackMin, fallbackMax, 'fallback');
            }
          }

          costRanges = this.applyProgressiveColors(costRanges);

          // Keep a stable default selection state so Apply always has colorized layer options.
          this.selected_cost_ranges = costRanges.filter((range) => range.selected && !range.disabled);

          this.total_cost_layers = Object.assign(layers).map((layer) => {
            if (costType === 'cost_per_kg' && (layer[costType] === null || layer[costType] === undefined)) {
              // No harvest on this parcel: no Cost/kg value, stays gray and unselected.
              layer.cost_range = null;
              return layer;
            }
            const layerCost = this.toCostNumber(layer[costType]);
            layer.cost_range = this.getConstRange(layerCost, costRanges);
            return layer;
          });

          this.onCostRangeSelectionChanged(this.selected_cost_ranges);

          if (this.selectedType === 'total_cost' || this.selectedType === 'cost_per_ha' || this.selectedType === 'cost_per_kg') {
            this.costRangeList = this.withRangeParcelCounts(costRanges, layers, costType);
          } else {
            this.costRangeList = costRanges;
          }
        }).catch((e) => {
          this.setColorFilterError(e);
        }).finally(() => {
          this.isCostDataLoading = false;
        });
      } else {
        this.isCostDataLoading = false;
      }
    },
    onCostRangeSelectionChanged(selectedCostRanges) {
      this.selected_cost_ranges = selectedCostRanges;
      this.total_cost_layers.map((layer) => {
        //for multi selection
        const costRange = selectedCostRanges.find(sr => sr.uuid === layer.cost_range?.uuid);
        layer.options = costRange ? getColorFilterOption(costRange.color) : getColorFilterOption('#D3D3D3');
        layer.selected = Boolean(costRange);
        layer.selected_type = this.selectedType;

        return layer;
      });
    },
    backToPrevStep() {
      this.$emit('onPrevStepSelect', {...this.currentSelectedType, back_path: this.currentTab, callback: this.onPrevStepFiltersApplied});
    },
    onPrevStepFiltersApplied() {
      const effectiveDateRange = this.dateRange_local || this.dateRange;
      const effectiveSelections =
        (Array.isArray(this.colorFilterItemsSelections_local) && this.colorFilterItemsSelections_local.length > 0)
          ? this.colorFilterItemsSelections_local
          : this.colorFilterItemsSelections;

      if (!effectiveDateRange || !Array.isArray(effectiveSelections) || effectiveSelections.length === 0) {
        this.total_cost_layers = [];
        this.costRangeList = [];
        this.selected_cost_ranges = [];
        return;
      }

      this.dateRange_local = effectiveDateRange;
      this.colorFilterItemsSelections_local = effectiveSelections;

      //if product
      let conditionProperty = {
        product_uuid: effectiveSelections[0]?.uuid
      };

      //if product type -  fito cat, fertility product type, or stock product type
      if (this.currentSelectedType.value === 'cost_product_type') {
        conditionProperty = {
          product_type_category_uuid: effectiveSelections[0]?.uuid
        };
      }

      this.loadCostTotalData(this.currentSelectedType.value, {...{
          date_from: effectiveDateRange?.date_from,
          date_to: effectiveDateRange?.date_to,
        }, ...conditionProperty});
    },
  },
  watch: {
    selectedType: function (newType, oldType) {
        if (newType !== oldType) {
          this.colorFilterItemsSelections_local = [];
          this.dateRange_local = null;
        }

        if (newType === 'total_cost' || newType === 'cost_per_ha' || newType === 'cost_per_kg') {
          this.loadCostTotalData(newType);
        } else {
          this.onPrevStepFiltersApplied(); //if prev step data changed, reload here
        }
    },
    dateRange: {
      handler: function (newVal) {
        this.dateRange_local = newVal;
      },
      deep: true,
    },
    colorFilterItemsSelections: {
      handler: function (newVal) {
        this.colorFilterItemsSelections_local = newVal;
      },
      deep: true,
    }
  }
}
</script>
